import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    fetchCitas,
    Cita,
    fetchPrecioPlanesServicios,
    fetchEspacioServicios,
    fetchCategories
} from '../../../services/citasService';
import { fetchCuponesUsados, CuponUsado } from '../../../services/usedCouponService';
import ExpiredSessionModal from '../../../components/Expired/ExpiredSessionModal';
import { useSessionExpiration } from '../../../hooks/useSessionExpiration';
import { useAuth } from '../../../context/AuthContext';
import { TicketPercent } from 'lucide-react';

const CitaCouponDetallePage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();

    const { isSessionExpired, setIsSessionExpired } = useSessionExpiration();
    const { logout } = useAuth();

    const [cita, setCita] = useState<Cita | null>(null);
    const [cuponUsado, setCuponUsado] = useState<CuponUsado | null>(null);
    const [nombrePlan, setNombrePlan] = useState<string>('—');
    const [espacioNombre, setEspacioNombre] = useState<string>('—');
    const [categoriaNombre, setCategoriaNombre] = useState<string>('—');

    useEffect(() => {
        const cargarDatos = async () => {
            const todasCitas = await fetchCitas();
            const detalle = todasCitas.find((c) => c.id === parseInt(id || '', 10));
            setCita(detalle || null);
            if (detalle) {
                const planes = await fetchPrecioPlanesServicios();
                const plan = planes.find((p) => p.id === detalle.precio_plan_servicio);
                if (plan) setNombrePlan(`${plan.plan_nombre} - ${plan.servicio_nombre}`);
                const espacios = await fetchEspacioServicios();
                const espacio = espacios.find((e) => e.id === detalle.espacio_servicio);
                if (espacio) setEspacioNombre(espacio.espacio_nombre);
                const categorias = await fetchCategories();
                const categoria = categorias.find((cat) => cat.id === detalle.categoria);
                if (categoria) setCategoriaNombre(categoria.nombre);
            }
            const cupones = await fetchCuponesUsados();
            const cupon = cupones.find((cu) => cu.cita_id === detalle?.id);
            setCuponUsado(cupon || null);
        };
        cargarDatos();
    }, [id]);
    
    return (
        <div className="max-w-7xl mx-auto p-8 bg-white shadow-xl rounded-lg space-y-8">
            <ExpiredSessionModal
                isOpen={isSessionExpired}
                onClose={async () => {
                    setIsSessionExpired(false);
                    await logout();
                }}
            />
            <div className="flex justify-between items-center">
                <h2 className="text-3xl font-bold text-gray-800">Detalle de la Cita con Cupones #{id}</h2>
                <button
                    onClick={() => navigate('/coupon-used')}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md shadow transition"
                >
                    ← Volver Atrás
                </button>
            </div>
            {cuponUsado ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 shadow-sm">
                        <h3 className="text-xl font-semibold text-blue-700 mb-4">Información de la Cita</h3>
                        <div className="space-y-2 text-gray-700 text-sm">
                            <p><strong>Fecha de Inicio:</strong>
                                {cita?.fecha_inicio
                                    ? new Date(cita.fecha_inicio).toLocaleDateString('es-BO', { day: 'numeric', month: 'long', year: 'numeric' })
                                    : '—'}
                            </p>
                            <p><strong>Fecha de Fin:</strong>
                                {cita?.fecha_fin
                                    ? new Date(cita.fecha_fin).toLocaleDateString('es-BO', { day: 'numeric', month: 'long', year: 'numeric' })
                                    : '—'}
                            </p>
                            <p><strong>Hora de Inicio:</strong> {cita?.hora_inicio ?? '—'}</p>
                            <p><strong>Hora de Fin:</strong> {cita?.hora_fin ?? '—'}</p>
                            <p><strong>Categoría:</strong> {categoriaNombre}</p>
                            <p><strong>Espacio:</strong> {espacioNombre}</p>
                            <p><strong>Plan y Servicio:</strong> {nombrePlan}</p>
                            <p><strong>Subtotal:</strong> Bs {cita?.subtotal ?? '—'}</p>
                            <p><strong>Descuento Directo:</strong> Bs {cita?.descuento_directo ?? 0}</p>
                            <p><strong>Total Final:</strong> <span className="text-green-600 font-semibold">Bs {cita?.total ?? '—'}</span></p>
                        </div>
                    </div>

                    {/* CUPÓN APLICADO */}
                    <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
                        <h3 className="text-xl font-semibold text-purple-700 mb-4">Cupón Aplicado</h3>
                        <div className="space-y-2 text-gray-700 text-sm">
                            <p><strong>Código:</strong> {cuponUsado.cupon_codigo}</p>
                            <p><strong>Cliente:</strong> {cuponUsado.cliente_nombre}</p>
                            {cuponUsado.cupon_descuento_monto !== null && (
                                <p><strong>Descuento:</strong> Bs {cuponUsado.cupon_descuento_monto} <span className="italic text-gray-500">(monto fijo)</span></p>
                            )}
                            {cuponUsado.cupon_descuento_porcentaje !== null && (
                                <p><strong>Descuento:</strong> {cuponUsado.cupon_descuento_porcentaje}% <span className="italic text-gray-500">(porcentaje)</span></p>
                            )}
                            <p><strong>Fecha de Uso:</strong> {new Date(cuponUsado.fecha_uso).toLocaleString()}</p>
                            <p><strong>Fecha de Registro:</strong> {new Date(cuponUsado.fecha_creacion).toLocaleString()}</p>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="py-24 text-center">
                    <div className="flex flex-col items-center justify-center space-y-6">
                        <TicketPercent className="w-20 h-20 text-gray-400" />
                        <h3 className="text-2xl font-semibold text-gray-700">No se aplicó ningún cupón en esta cita</h3>
                        <p className="text-gray-500 text-sm max-w-lg">
                            Esta cita no tiene asociado ningún cupón. Cuando se registre un cupón para esta cita, la información detallada se mostrará automáticamente aquí.
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
}

export default CitaCouponDetallePage;
